import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../../context/AuthContext';
import * as adminUserService from '../../services/adminUserService';
import { useStrings } from '../../i18n';

export default function AdminUsers() {
  const strings = useStrings();
  const { getIdToken, userInfo } = useAuth();

  // State management
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filtering & Search
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  // Modal states
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [demoteModalOpen, setDemoteModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  // Form data
  const [selectedUser, setSelectedUser] = useState(null);
  const [promoteFormData, setPromoteFormData] = useState({
    role: 'admin',
    reason: '',
  });
  const [demoteFormData, setDemoteFormData] = useState({
    reason: '',
  });

  // Fetch admins on mount and when filters change
  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm, roleFilter]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError('');
      const idToken = await getIdToken();

      const params = {
        page,
        limit: 25,
        search: searchTerm,
        role: roleFilter === 'all' ? undefined : roleFilter,
      };

      // Remove undefined values
      Object.keys(params).forEach((key) =>
        params[key] === undefined && delete params[key]
      );

      const response = await adminUserService.listAdmins(params, idToken);
      setAdmins(response.data || []);
      setPagination(response.pagination || {});
    } catch (err) {
      setError(err.message || strings.admin?.error || 'Error loading admins');
      console.error('Error fetching admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteClick = (admin) => {
    setSelectedUser(admin);
    setPromoteFormData({ role: 'admin', reason: '' });
    setPromoteModalOpen(true);
  };

  const handleDemoteClick = (admin) => {
    setSelectedUser(admin);
    setDemoteFormData({ reason: '' });
    setDemoteModalOpen(true);
  };

  const handleViewDetails = async (admin) => {
    try {
      const idToken = await getIdToken();
      const response = await adminUserService.getAdmin(admin.uid, idToken);
      setSelectedUser(response.data);
      setDetailsModalOpen(true);
    } catch (err) {
      setError(err.message || 'Error loading admin details');
    }
  };

  const handlePromote = async () => {
    try {
      setError('');
      const idToken = await getIdToken();
      await adminUserService.promoteUser(
        selectedUser.uid,
        promoteFormData,
        idToken
      );
      setSuccess(strings.admin?.promoteSuccess || 'User promoted successfully');
      setPromoteModalOpen(false);
      fetchAdmins();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Error promoting user');
    }
  };

  const handleDemote = async () => {
    try {
      setError('');
      const idToken = await getIdToken();
      await adminUserService.demoteUser(
        selectedUser.uid,
        demoteFormData,
        idToken
      );
      setSuccess(strings.admin?.demoteSuccess || 'User demoted successfully');
      setDemoteModalOpen(false);
      fetchAdmins();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Error demoting user');
    }
  };

  return (
    <div className="w-full bg-primaryBg min-h-screen">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">
        {/* Back Button */}
        <Link
          to="/admin"
          className="inline-flex items-center text-secondary hover:text-secondary/80 mb-6 transition-colors"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {strings.admin?.backToDashboard || 'Back to Dashboard'}
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading text-headingText mb-2">
            {strings.admin?.manageAdmins || 'Manage Admins'}
          </h1>
          <p className="text-textSecondary">
            {strings.admin?.adminUsers ||
              'View, promote, and manage admin users'}
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {success}
          </div>
        )}

        {/* Filters Section */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          {/* Search */}
          <input
            type="text"
            placeholder={strings.admin?.searchByEmail || 'Search by email...'}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="flex-1 px-4 py-2 border border-secondary/20 rounded-lg bg-surface text-bodyText focus:outline-none focus:border-secondary"
          />

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-secondary/20 rounded-lg bg-surface text-bodyText focus:outline-none focus:border-secondary"
          >
            <option value="all">
              {strings.admin?.allRoles || 'All Roles'}
            </option>
            <option value="admin">
              {strings.admin?.roleAdmin || 'Admin'}
            </option>
            <option value="superadmin">
              {strings.admin?.roleSuperadmin || 'Superadmin'}
            </option>
          </select>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <p className="text-textSecondary">
              {strings.admin?.loading || 'Loading...'}
            </p>
          </div>
        )}

        {/* Admins Table */}
        {!loading && admins.length === 0 && (
          <div className="text-center py-12">
            <p className="text-textSecondary">
              {strings.admin?.noAdminsFound || 'No admin users found'}
            </p>
          </div>
        )}

        {!loading && admins.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary/20">
                  <th className="text-left py-3 px-4 font-semibold text-bodyText">
                    {strings.auth?.email || 'Email'}
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-bodyText">
                    {strings.admin?.fullName || 'Name'}
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-bodyText">
                    {strings.admin?.status || 'Role'}
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-bodyText">
                    {strings.admin?.dateAssigned || 'Date Assigned'}
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-bodyText">
                    {strings.admin?.actions || 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin, index) => (
                  <tr
                    key={admin.uid}
                    className={`border-b border-secondary/10 hover:bg-cardBg/50 transition-colors ${
                      index % 2 === 0 ? 'bg-surface/30' : ''
                    }`}
                  >
                    <td className="py-3 px-4 text-bodyText">{admin.email}</td>
                    <td className="py-3 px-4 text-bodyText">
                      {admin.displayName || '—'}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          admin.role === 'superadmin'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {admin.role === 'superadmin'
                          ? strings.admin?.roleSuperadmin || 'Superadmin'
                          : strings.admin?.roleAdmin || 'Admin'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-bodyText text-sm">
                      {admin.adminAssignedAt
                        ? new Date(admin.adminAssignedAt).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewDetails(admin)}
                          className="px-2 py-1 text-xs bg-secondary text-white rounded hover:bg-secondary/80 transition-colors"
                        >
                          {strings.admin?.viewDetails || 'Details'}
                        </button>
                        {userInfo?.role === 'superadmin' && admin.role !== 'superadmin' && (
                          <button
                            onClick={() => handlePromoteClick(admin)}
                            className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                          >
                            {strings.admin?.promoteUser || 'Promote'}
                          </button>
                        )}
                        {userInfo?.role === 'superadmin' && admin.role !== 'user' && (
                          <button
                            onClick={() => handleDemoteClick(admin)}
                            className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                          >
                            {strings.admin?.demoteUser || 'Demote'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && pagination.total > 25 && (
          <div className="mt-6 flex justify-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-4 py-2 border border-secondary/20 rounded-lg hover:bg-cardBg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {strings.admin?.previous || 'Previous'}
            </button>
            <span className="px-4 py-2 text-bodyText">
              {strings.admin?.page || 'Page'} {page}
            </span>
            <button
              disabled={
                !pagination.total ||
                page * 25 >= pagination.total
              }
              onClick={() => setPage(page + 1)}
              className="px-4 py-2 border border-secondary/20 rounded-lg hover:bg-cardBg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {strings.admin?.next || 'Next'}
            </button>
          </div>
        )}
      </div>

      {/* Promote Modal */}
      {promoteModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-2xl font-heading text-headingText mb-4">
              {strings.admin?.promoteUser || 'Promote User'}
            </h2>

            <p className="text-textSecondary mb-4">
              {selectedUser.displayName} ({selectedUser.email})
            </p>

            {/* Role Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-bodyText mb-2">
                {strings.admin?.selectTargetRole || 'Target Role'}
              </label>
              <select
                value={promoteFormData.role}
                onChange={(e) =>
                  setPromoteFormData({
                    ...promoteFormData,
                    role: e.target.value,
                  })
                }
                className="w-full px-4 py-2 border border-secondary/20 rounded-lg bg-primaryBg text-bodyText focus:outline-none focus:border-secondary"
              >
                <option value="admin">
                  {strings.admin?.roleAdmin || 'Admin'}
                </option>
                <option value="superadmin">
                  {strings.admin?.roleSuperadmin || 'Superadmin'}
                </option>
              </select>
            </div>

            {/* Reason Input */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-bodyText mb-2">
                {strings.admin?.promoteReason || 'Reason (optional)'}
              </label>
              <textarea
                value={promoteFormData.reason}
                onChange={(e) =>
                  setPromoteFormData({
                    ...promoteFormData,
                    reason: e.target.value,
                  })
                }
                placeholder="e.g., Promoted to team lead"
                className="w-full px-4 py-2 border border-secondary/20 rounded-lg bg-primaryBg text-bodyText focus:outline-none focus:border-secondary"
                rows="3"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setPromoteModalOpen(false)}
                className="flex-1 px-4 py-2 border border-secondary/20 rounded-lg text-bodyText hover:bg-cardBg transition-colors"
              >
                {strings.admin?.cancel || 'Cancel'}
              </button>
              <button
                onClick={handlePromote}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold"
              >
                {strings.admin?.promoteUser || 'Promote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Demote Confirmation Modal */}
      {demoteModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-2xl font-heading text-headingText mb-4">
              {strings.admin?.confirmDemote || 'Confirm Demotion'}
            </h2>

            <p className="text-textSecondary mb-4">
              {strings.admin?.demoteWarning ||
                'This user will lose all admin access and permissions.'}
            </p>

            <div className="mb-4">
              <p className="text-sm font-semibold text-bodyText mb-2">
                {selectedUser.displayName} ({selectedUser.email})
              </p>
            </div>

            {/* Reason Input */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-bodyText mb-2">
                {strings.admin?.demoteReason || 'Reason (optional)'}
              </label>
              <textarea
                value={demoteFormData.reason}
                onChange={(e) =>
                  setDemoteFormData({
                    ...demoteFormData,
                    reason: e.target.value,
                  })
                }
                placeholder="e.g., End of employment"
                className="w-full px-4 py-2 border border-secondary/20 rounded-lg bg-primaryBg text-bodyText focus:outline-none focus:border-secondary"
                rows="3"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setDemoteModalOpen(false)}
                className="flex-1 px-4 py-2 border border-secondary/20 rounded-lg text-bodyText hover:bg-cardBg transition-colors"
              >
                {strings.admin?.cancel || 'Cancel'}
              </button>
              <button
                onClick={handleDemote}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold"
              >
                {strings.admin?.demoteUser || 'Demote User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {detailsModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl max-w-md w-full mx-4 p-6 max-h-96 overflow-y-auto">
            <h2 className="text-2xl font-heading text-headingText mb-4">
              {strings.admin?.viewDetails || 'Admin Details'}
            </h2>

            <div className="space-y-4 mb-6">
              <div>
                <p className="text-xs text-textSecondary">
                  {strings.admin?.email || 'Email'}
                </p>
                <p className="font-semibold text-bodyText">
                  {selectedUser.email}
                </p>
              </div>

              <div>
                <p className="text-xs text-textSecondary">
                  {strings.admin?.fullName || 'Name'}
                </p>
                <p className="font-semibold text-bodyText">
                  {selectedUser.displayName || '—'}
                </p>
              </div>

              <div>
                <p className="text-xs text-textSecondary">
                  {strings.admin?.status || 'Role'}
                </p>
                <p className="font-semibold text-bodyText capitalize">
                  {selectedUser.role}
                </p>
              </div>

              <div>
                <p className="text-xs text-textSecondary">
                  {strings.admin?.dateAssigned || 'Date Assigned'}
                </p>
                <p className="font-semibold text-bodyText">
                  {selectedUser.adminAssignedAt
                    ? new Date(
                        selectedUser.adminAssignedAt
                      ).toLocaleDateString()
                    : '—'}
                </p>
              </div>

              <div>
                <p className="text-xs text-textSecondary">
                  {strings.admin?.assignedBy || 'Assigned By'}
                </p>
                <p className="font-semibold text-bodyText">
                  {selectedUser.adminAssignedBy || '—'}
                </p>
              </div>

              {/* Status History */}
              {selectedUser.adminStatusHistory &&
                selectedUser.adminStatusHistory.length > 0 && (
                  <div>
                    <p className="text-xs text-textSecondary mb-2">
                      {strings.admin?.statusHistory || 'Status History'}
                    </p>
                    <div className="space-y-2">
                      {selectedUser.adminStatusHistory.map((entry, idx) => (
                        <div
                          key={idx}
                          className="text-xs bg-cardBg p-2 rounded border border-secondary/10"
                        >
                          <p className="font-semibold capitalize">
                            {entry.status}
                          </p>
                          <p className="text-textSecondary">
                            {new Date(entry.changedAt).toLocaleDateString()} by{' '}
                            {entry.changedBy}
                          </p>
                          {entry.reason && (
                            <p className="text-textSecondary mt-1">
                              {entry.reason}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            {/* Close Button */}
            <button
              onClick={() => setDetailsModalOpen(false)}
              className="w-full px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/80 transition-colors font-semibold"
            >
              {strings.admin?.close || 'Close'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
